import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, Eye, UserCheck, FileText, Calendar, MapPin, BookOpen, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminStudentManagementPage = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [assigningTutor, setAssigningTutor] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [updateForm, setUpdateForm] = useState({
    status: '',
    assignedTutorId: '',
    adminNotes: ''
  });

  const fetchEnrollments = useCallback(async () => {
    try {
      // FIXED: Use V4 endpoint to match booking system (Phase 3A fix)
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/students`, { 
        credentials: 'include' 
      });
      
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      
      // Map V4 backend schema to frontend expectations
      const mappedEnquiries = (data.students || []).map(enq => ({
        ...enq,
        id: enq.id,
        studentFullName: enq.student_name,
        parentGuardianName: enq.parent_name || '',
        email: enq.email,
        phone: enq.phone,
        service: enq.program_type,
        subject: enq.subject,
        yearLevel: enq.year_level,
        preferredFormat: enq.preferred_format,
        preferredDaysTimes: enq.preferred_times,
        requestedTutorName: enq.requested_tutor_name || null,
        tutorSelectionType: enq.tutor_preference,
        goals: enq.notes || '',
        currentChallenges: enq.notes || '',
        status: enq.status,
        assignedTutorName: enq.assigned_tutor_name || null,
        submittedAt: enq.submitted_at,
        adminNotes: enq.admin_notes || ''
      }));
      
      // Filter by status if needed
      const filtered = statusFilter === 'All' 
        ? mappedEnquiries 
        : mappedEnquiries.filter(e => e.status === statusFilter.toLowerCase());
      
      setEnrollments(filtered);
    } catch (error) {
      toast.error('Failed to fetch enrollments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchEnrollments();
    fetchTutors();
  }, [fetchEnrollments]);

  const fetchTutors = async () => {
    try {
      // FIXED: Use V4 endpoint (Phase 3A fix)
      const response = await fetch(`${BACKEND_URL}/api/v4/public/tutors`);
      const data = await response.json();
      if (data.success) {
        setTutors(data.tutors || []);
      }
    } catch (error) {
      // Log error in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch tutors:', error);
      }
      // Fallback to empty array if fetch fails
      setTutors([]);
    }
  };

  const handleViewDetails = async (enrollmentId) => {
    try {
      // FIXED: Fetch from V4 students endpoint (Phase 3A fix)
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/students`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        const enrollment = data.students.find(s => s.id === enrollmentId);
        if (!enrollment) {
          toast.error('Enquiry not found');
          return;
        }
        
        // Map V4 schema to frontend expectations
        const mappedEnrollment = {
          ...enrollment,
          id: enrollment.id,
          studentFullName: enrollment.student_name,
          parentGuardianName: enrollment.parent_name || '',
          email: enrollment.email,
          phone: enrollment.phone,
          service: enrollment.program_type,
          subject: enrollment.subject,
          yearLevel: enrollment.year_level,
          preferredFormat: enrollment.preferred_format,
          preferredDaysTimes: enrollment.preferred_times,
          requestedTutorName: enrollment.requested_tutor_name || null,
          tutorSelectionType: enrollment.tutor_preference,
          goals: enrollment.notes || '',
          currentChallenges: enrollment.notes || '',
          status: enrollment.status,
          assignedTutorName: enrollment.assigned_tutor_name || null,
          assignedTutorId: enrollment.assigned_tutor_id || '',
          submittedAt: enrollment.submitted_at,
          adminNotes: enrollment.admin_notes || ''
        };
        
        setSelectedEnrollment(mappedEnrollment);
        setUpdateForm({
          status: mappedEnrollment.status,
          assignedTutorId: mappedEnrollment.assignedTutorId || '',
          adminNotes: mappedEnrollment.adminNotes || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load enrollment details');
    }
  };

  const handleUpdateEnrollment = async () => {
    if (!selectedEnrollment) return;

    setUpdatingStatus(true);
    try {
      // FIXED: Use V4 endpoint (Phase 3A fix)
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/students/${selectedEnrollment.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          status: updateForm.status,
          admin_notes: updateForm.adminNotes
        })
      });

      if (!response.ok) throw new Error('Update failed');

      toast.success('Enrollment updated successfully');
      
      // Refresh data
      fetchEnrollments();
      handleViewDetails(selectedEnrollment.id);
    } catch (error) {
      toast.error('Failed to update enrollment');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssignTutor = async () => {
    if (!selectedEnrollment || !updateForm.assignedTutorId) {
      toast.error('Please select a tutor');
      return;
    }

    setAssigningTutor(true);
    try {
      // FIXED: Use V4 endpoint (Phase 3A fix)
      const formData = new FormData();
      formData.append('tutor_id', updateForm.assignedTutorId);
      
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/students/${selectedEnrollment.id}/assign`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      if (!response.ok) throw new Error('Assignment failed');

      toast.success('Tutor assigned successfully');
      fetchEnrollments();
      handleViewDetails(selectedEnrollment.id);
    } catch (error) {
      toast.error('Failed to assign tutor');
    } finally {
      setAssigningTutor(false);
    }
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      enrollment.studentFullName?.toLowerCase().includes(search) ||
      enrollment.email?.toLowerCase().includes(search) ||
      enrollment.subject?.toLowerCase().includes(search) ||
      enrollment.requestedTutorName?.toLowerCase().includes(search) ||
      enrollment.assignedTutorName?.toLowerCase().includes(search)
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      'New Enquiry': 'bg-blue-100 text-blue-800',
      'Awaiting Match': 'bg-yellow-100 text-yellow-800',
      'Tutor Requested': 'bg-purple-100 text-purple-800',
      'Tutor Assigned': 'bg-green-100 text-green-800',
      'Trial Booked': 'bg-indigo-100 text-indigo-800',
      'Active Student': 'bg-emerald-100 text-emerald-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTutorName = (tutorId) => {
    const tutor = tutors.find(t => t.id === tutorId);
    return tutor ? tutor.fullName : 'Unknown Tutor';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" 
            style={{ borderColor: 'var(--accent-teal)' }}></div>
          <p style={{ color: 'var(--text-grey)' }}>Loading enrollments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="premium-gradient text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="heading-xl mb-2">Student Management</h1>
              <p className="opacity-95">Manage student enrollments and tutor assignments</p>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full">
              <Users className="w-5 h-5" />
              <span className="font-semibold">{enrollments.length} Total Requests</span>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex gap-2">
            <Button onClick={() => window.location.href = '/admin/applications'} 
              className="bg-white/20 hover:bg-white/30 backdrop-blur">
              Tutor Applications
            </Button>
            <Button onClick={() => window.location.href = '/admin/payments'} 
              className="bg-white/20 hover:bg-white/30 backdrop-blur">
              Payments
            </Button>
            <Button onClick={() => window.location.href = '/admin/migrations'} 
              className="bg-white/20 hover:bg-white/30 backdrop-blur">
              Migrations
            </Button>
            <Button onClick={() => window.location.href = '/'} 
              className="bg-white/20 hover:bg-white/30 backdrop-blur">
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Enrollment List */}
          <div className="lg:col-span-2">
            <Card className="premium-card p-6">
              {/* Filters */}
              <div className="mb-6 space-y-4">
                <div>
                  <Label className="form-label">Search Enrollments</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" 
                      style={{ color: 'var(--text-grey)' }} />
                    <Input
                      placeholder="Search by student name, email, subject..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label className="form-label">Filter by Status</Label>
                  <select
                    className="w-full px-4 py-2 rounded-md border"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ borderColor: 'var(--border-color)' }}
                  >
                    <option value="All">All Statuses</option>
                    <option value="New Enquiry">New Enquiry</option>
                    <option value="Awaiting Match">Awaiting Match</option>
                    <option value="Tutor Requested">Tutor Requested</option>
                    <option value="Tutor Assigned">Tutor Assigned</option>
                    <option value="Trial Booked">Trial Booked</option>
                    <option value="Active Student">Active Student</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Enrollment Cards */}
              <div className="space-y-4">
                {filteredEnrollments.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-grey)' }} />
                    <p style={{ color: 'var(--text-grey)' }}>No enrollments found</p>
                  </div>
                ) : (
                  filteredEnrollments.map((enrollment) => (
                    <div
                      key={enrollment.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedEnrollment?.id === enrollment.id
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5'
                          : 'border-[var(--border-color)] hover:border-[var(--accent-teal)]'
                      }`}
                      onClick={() => handleViewDetails(enrollment.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{enrollment.studentFullName}</h3>
                          <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                            {enrollment.email} • {enrollment.phone}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(enrollment.status)}`}>
                          {enrollment.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                          <span>{enrollment.programType} - {enrollment.subject}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                          <span>{enrollment.yearLevel}</span>
                        </div>
                        {enrollment.requestedTutorName && (
                          <div className="flex items-center gap-2 col-span-2">
                            <UserCheck className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                            <span><strong>Preferred:</strong> {enrollment.requestedTutorName}</span>
                          </div>
                        )}
                        {!enrollment.requestedTutorName && enrollment.tutorSelectionType === 'match' && (
                          <div className="flex items-center gap-2 col-span-2">
                            <UserCheck className="w-4 h-4" style={{ color: '#999' }} />
                            <span style={{ color: '#666' }}><strong>Preferred:</strong> No Preference</span>
                          </div>
                        )}
                        {enrollment.assignedTutorName && (
                          <div className="flex items-center gap-2 col-span-2">
                            <UserCheck className="w-4 h-4" style={{ color: 'var(--success-green)' }} />
                            <span><strong>Assigned:</strong> {enrollment.assignedTutorName}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 text-xs" style={{ color: 'var(--text-grey)' }}>
                        Submitted: {new Date(enrollment.submittedAt).toLocaleDateString('en-AU', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Enrollment Details Panel */}
          <div>
            {selectedEnrollment ? (
              <Card className="premium-card p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="heading-md">Enrollment Details</h2>
                  <Button
                    onClick={() => setSelectedEnrollment(null)}
                    className="text-sm"
                    variant="ghost"
                  >
                    Close
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Student Info */}
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Student Information
                    </h3>
                    <div className="space-y-1 text-sm">
                      <p><strong>Student:</strong> {selectedEnrollment.studentFullName}</p>
                      {selectedEnrollment.parentGuardianName && (
                        <p><strong>Parent/Guardian:</strong> {selectedEnrollment.parentGuardianName}</p>
                      )}
                      <p><strong>Email:</strong> {selectedEnrollment.email}</p>
                      <p><strong>Phone:</strong> {selectedEnrollment.phone}</p>
                      {selectedEnrollment.suburb && (
                        <p><strong>Location:</strong> {selectedEnrollment.suburb}, {selectedEnrollment.city}, {selectedEnrollment.state}</p>
                      )}
                    </div>
                  </div>

                  {/* Academic Info */}
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Academic Details
                    </h3>
                    <div className="space-y-1 text-sm">
                      <p><strong>Program:</strong> {selectedEnrollment.programType}</p>
                      <p><strong>Subject:</strong> {selectedEnrollment.subject}</p>
                      <p><strong>Year Level:</strong> {selectedEnrollment.yearLevel}</p>
                      <p><strong>Goals:</strong> {selectedEnrollment.goals}</p>
                      <p><strong>Challenges:</strong> {selectedEnrollment.currentChallenges}</p>
                    </div>
                  </div>

                  {/* Preferences */}
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Preferences
                    </h3>
                    <div className="space-y-1 text-sm">
                      <p><strong>Format:</strong> {selectedEnrollment.preferredFormat}</p>
                      <p><strong>Availability:</strong> {selectedEnrollment.preferredDaysTimes}</p>
                      {selectedEnrollment.additionalNotes && (
                        <p><strong>Notes:</strong> {selectedEnrollment.additionalNotes}</p>
                      )}
                    </div>
                  </div>

                  {/* Tutor Assignment */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Tutor Assignment
                    </h3>
                    
                    {/* Preferred Tutor (Student's Choice) */}
                    {selectedEnrollment.tutorSelectionType === 'specific' && selectedEnrollment.requestedTutorName && (
                      <div className="mb-4 p-3 rounded border-2" style={{ backgroundColor: '#EBF5FF', borderColor: '#3B82F6' }}>
                        <p className="text-sm font-semibold text-blue-900">Preferred Tutor:</p>
                        <p className="text-sm text-gray-900 mt-1">{selectedEnrollment.requestedTutorName}</p>
                        <p className="text-xs text-blue-700 mt-1">Selected by student</p>
                      </div>
                    )}
                    {selectedEnrollment.tutorSelectionType === 'match' && (
                      <div className="mb-4 p-3 rounded border-2" style={{ backgroundColor: '#F3F4F6', borderColor: '#9CA3AF' }}>
                        <p className="text-sm font-semibold text-gray-700">Preferred Tutor:</p>
                        <p className="text-sm text-gray-600 mt-1">No Preference</p>
                        <p className="text-xs text-gray-500 mt-1">Student requested best match</p>
                      </div>
                    )}

                    {/* Assigned Tutor (Admin Control) */}
                    <div className="mb-3">
                      <Label className="form-label text-sm font-semibold">Assigned Tutor (Admin Control):</Label>
                      <select
                        className="w-full px-3 py-2 rounded-md border text-sm mt-1"
                        value={updateForm.assignedTutorId}
                        onChange={(e) => setUpdateForm(prev => ({ ...prev, assignedTutorId: e.target.value }))}
                        style={{ borderColor: 'var(--border-color)' }}
                      >
                        <option value="">Select a tutor...</option>
                        {tutors.map(tutor => (
                          <option key={tutor.id} value={tutor.id}>
                            {tutor.fullName} - {tutor.subjects?.slice(0, 2).join(', ')}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedEnrollment.assignedTutorName && (
                      <div className="mb-3 p-3 rounded border-2" style={{ backgroundColor: '#DCFCE7', borderColor: '#16A34A' }}>
                        <p className="text-sm font-semibold text-green-900">Assigned Tutor:</p>
                        <p className="text-sm text-gray-900 mt-1">{selectedEnrollment.assignedTutorName}</p>
                        <p className="text-xs text-green-700 mt-1">Allocated by Winhouse Tutors</p>
                      </div>
                    )}
                  </div>

                  {/* Status Update */}
                  <div>
                    <Label className="form-label text-sm">Update Status</Label>
                    <select
                      className="w-full px-3 py-2 rounded-md border text-sm mb-3"
                      value={updateForm.status}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, status: e.target.value }))}
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <option value="New Enquiry">New Enquiry</option>
                      <option value="Awaiting Match">Awaiting Match</option>
                      <option value="Tutor Requested">Tutor Requested</option>
                      <option value="Tutor Assigned">Tutor Assigned</option>
                      <option value="Trial Booked">Trial Booked</option>
                      <option value="Active Student">Active Student</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <Label className="form-label text-sm">Admin Notes (Internal)</Label>
                    <Textarea
                      value={updateForm.adminNotes}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, adminNotes: e.target.value }))}
                      rows={4}
                      placeholder="Add internal notes about this enrollment..."
                      className="text-sm"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {updateForm.assignedTutorId && updateForm.assignedTutorId !== selectedEnrollment.assignedTutorId && (
                      <Button
                        onClick={handleAssignTutor}
                        disabled={assigningTutor}
                        className="w-full premium-button"
                      >
                        {assigningTutor ? 'Assigning...' : 'Assign Tutor'}
                      </Button>
                    )}
                    
                    <Button
                      onClick={handleUpdateEnrollment}
                      disabled={updatingStatus}
                      className="w-full premium-button-secondary"
                    >
                      {updatingStatus ? 'Updating...' : 'Update Enrollment'}
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="premium-card p-8 text-center">
                <Eye className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-grey)' }} />
                <p style={{ color: 'var(--text-grey)' }}>
                  Select an enrollment to view details and manage assignment
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStudentManagementPage;
